import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@shape-ds/components';
import { buildDateColumns } from 'components/ExternalReferencesTables/columsConfig';
import Filters from 'components/ExternalReferencesTables/Filters/Filters';
import { useOilAnalysisQuery } from 'queries/externalReferences/queries';
import { buildColumnsObject } from '@shape/lighthouse-components';
import { sortDates } from 'utils/DateUtil';
import { OilData } from 'queries/externalReferences/contract';
import { DEFAULT_PERIOD } from './MaintenanceNotesTable';
import TableDataContainer from './TableData.container';
import { EVIDENCE_DEFAULT_PERIOD } from 'components/Evidences/hooks/useOilAnalysis';

interface OilAnalysisTableProps {
  assetId: string;
  className?: string;
  maxHeight?: number;
  isEvidenceTable?: boolean;
}

const OilAnalysisTable: React.FC<OilAnalysisTableProps> = ({
  assetId,
  className,
  maxHeight,
  isEvidenceTable = false,
}) => {
  const [period, setPeriod] = useState<string>(
    isEvidenceTable ? EVIDENCE_DEFAULT_PERIOD : DEFAULT_PERIOD,
  );
  const { data, isLoading } = useOilAnalysisQuery(assetId, period);
  const [filteredData, setFilteredData] = useState<OilData[]>(data ?? []);
  const { t } = useTranslation();

  //pegar endpoint oilanalysis table edited, se isEvidenceTable for true fazer a chamada para esse endpoint e usar ele ao invés do filteredData

  useMemo(() => {
    setFilteredData(data ?? []);
  }, [data]);

  const searchKeys =
    (data?.length ?? 0) > 0
      ? [...new Set(data?.map((row) => Object.keys(row)[0]))]
      : ['description'];

  const dinamicColumns = useMemo(() => {
    if (data && data.length > 0) {
      const oilColumns = Object.keys(data[0]);

      return oilColumns.filter((key) => !/[a-zA-Z]/.test(key)).sort(sortDates);
    }

    return [];
  }, [data]);
  const columns = [
    ...buildColumnsObject<OilData>({
      columns: {
        description: {
          enableSorting: false,
          size: 200,
          header: t('monitoring:tables.columns.OIL_ANALYSIS.description'),
          accessorFn: (data: OilData) =>
            data[Object.keys(data).find((key) => /[a-zA-Z]/.test(key)) ?? 0],
        },
      },
      getLabel: (key: string) => t(`monitoring:tables.columns.OIL_ANALYSIS.${key}`),
    }),
    ...buildDateColumns<OilData>(dinamicColumns),
  ];

  return (
    <TableDataContainer
      isLoading={isLoading}
      dataLength={data?.length}
      className={`oil-analysis ${className}`}
      type="OIL_ANALYSIS"
    >
      {!isEvidenceTable && (
        <Filters<OilData>
          data={data ?? []}
          onChangeFilters={(newData) => setFilteredData(newData)}
          setPeriod={setPeriod}
          period={period}
          searchKey={searchKeys}
          enableColumnFilter
        />
      )}

      {data && data.length > 0 && (
        <Table<OilData>
          style={isEvidenceTable ? { maxHeight: '54vh', maxWidth: '66.5vw' } : { maxHeight }}
          data={filteredData}
          className="table"
          columns={columns}
          isLoading={isLoading}
        />
      )}
    </TableDataContainer>
  );
};

export default OilAnalysisTable;
